import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, Loader2, Sparkles, X } from "lucide-react";
import { explainKpi, track } from "../lib/api";

/**
 * Compact "Why?" button placed next to a KPI / chart.
 * Renders the popover into document.body via a portal so parent
 * overflow:hidden / clipping (KpiCard gradient mask) cannot crop it.
 */
export default function ExplainButton({ kpi, context = null, label = "למה?", className = "" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const POP_W = 288;

  const recompute = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const top = r.bottom + window.scrollY + 6;
    // anchor popover to the right edge of the button (RTL natural feel)
    let left = r.right + window.scrollX - POP_W;
    if (left < 8) left = 8;
    if (left + POP_W > window.innerWidth - 8) left = window.innerWidth - POP_W - 8;
    setPos({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    recompute();
    const onScroll = () => recompute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const fetchExplain = async () => {
    setLoading(true);
    setErr("");
    setText("");
    track("kpi_explain_open", null, { kpi });
    try {
      const { data } = await explainKpi(kpi, context);
      setText(data.text || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "ה-AI לא הצליח להסביר כרגע. נסה שוב.");
    } finally {
      setLoading(false);
    }
  };

  const onTrigger = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!open) fetchExplain();
    setOpen((o) => !o);
  };

  const popover = open ? (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[80]"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(false);
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: -4, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -4, scale: 0.98 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        style={{ position: "absolute", top: pos.top, left: pos.left, width: POP_W }}
        className="z-[85] bg-card border border-border rounded-lg shadow-2xl p-3 text-right"
        dir="rtl"
        data-testid={`explain-popover-${kpi}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-24 -right-12 w-40 h-40 bg-primary/8 rounded-full blur-3xl pointer-events-none" />

        <div className="flex items-center justify-between mb-2 relative">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-primary" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              Optygo Pulse · הסבר
            </span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="text-zinc-500 hover:text-foreground p-0.5"
            aria-label="סגור"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="relative min-h-[60px]">
          {loading && (
            <div className="flex items-center gap-2 text-[11px] text-zinc-400 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              <span>ה-AI מחשב הסבר...</span>
            </div>
          )}
          {err && !loading && (
            <div className="text-[11px] text-severity-critical leading-relaxed">{err}</div>
          )}
          {text && !loading && (
            <div className="text-xs text-zinc-200 leading-relaxed whitespace-pre-wrap">
              {text}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={onTrigger}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border bg-[#0A0A0E] hover:border-primary/40 hover:text-primary text-zinc-500 text-[10px] font-mono transition-colors ${className}`}
        data-testid={`explain-${kpi}`}
        title="הסבר AI על המספר הזה"
      >
        <HelpCircle className="w-2.5 h-2.5" strokeWidth={1.75} />
        {label}
      </button>
      {typeof document !== "undefined" && popover && createPortal(popover, document.body)}
    </>
  );
}
